import React, { useEffect, useRef, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import { init } from 'emoji-mart'


import './EmojiPicker.scss'

export const EmojiPicker = ({ onSelect, strings, variant, disabled }) => {
  const [open, setOpen] = useState(false)
  const element = useRef(null)

  const { current: handleClickOutside } = useRef(e => {
    if (element.current && !element.current.contains(e.target)) {
      setOpen(false)
    }
  })

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    const clear = () => {
      document.removeEventListener('click', handleClickOutside)
    }
    return clear
  }, [])

  useEffect(() => {
    init({ data })
  }, [])
  
  const toggle = useCallback(() => {
    !disabled && setOpen(prev => !prev)
  }, [disabled])

  return (
    <div
      className={classnames('c-emoji-picker', {
        'c-emoji-picker--visible': open,
        'c-emoji-picker--danger': variant === 'danger' && !disabled,
        'c-emoji-picker--disabled': disabled
      })}
      ref={element}
    >
      <button className='c-emoji-picker__button' onClick={toggle}>
        <em-emoji shortcodes=":grin:" size={18}></em-emoji>
      </button>
      <div className='c-emoji-picker__picker'>
        <Picker
          data={data}
          i18n={strings}
          onEmojiSelect={onSelect}
          categories={['frequent', 'people', 'nature', 'foods', 'activity', 'places', 'objects', 'symbols']}
        />
      </div>
    </div>
  )
}

EmojiPicker.defaultProps = {
  variant: undefined,
  disabled: false
}

EmojiPicker.propTypes = {
  onSelect: PropTypes.func,
  strings: PropTypes.object,
  variant: PropTypes.string,
  disabled: PropTypes.bool
}

export const makeInputWithEmojiPicker = InputComponent => ({
  onChange,
  onClick,
  onSelectEmoticon,
  variant,
  disabled,
  ...props
}) => {
  const [active, setActive] = useState(false)
  const ref = useRef(null)
  const elInputRef = useRef(null)
  const selectionStart = useRef(null)
  const selectionEnd = useRef(null)

  const elInputWrapperRef = el => {
    if (!el) {
      return
    }

    elInputRef.current = el.getElementsByTagName('input')[0]
  }

  const { current: handleClickOutside } = useRef(e => {
    if (ref.current && !ref.current.contains(e.target)) {
      selectionStart.current = selectionEnd.current =
        elInputRef.current.value.length
      setActive(false)
    }
  })

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    const clear = () => {
      document.removeEventListener('click', handleClickOutside)
    }
    return clear
  }, [])

  const updateSelection = () => {
    selectionStart.current = elInputRef.current.selectionStart
    selectionEnd.current = elInputRef.current.selectionEnd
  }

  const handleClickInputComponent = e => {
    if (typeof onClick === 'function') {
      onClick(e)
    }
    updateSelection()
  }

  const handleChangeInputComponent = e => {
    if (typeof onChange === 'function') {
      onChange(e)
    }
    updateSelection()
  }

  const handleSelect = emoji => {
    elInputRef.current.setRangeText(
      emoji.native,
      selectionStart.current,
      selectionEnd.current,
      'end'
    )

    var event = new Event('input', { bubbles: true })
    elInputRef.current.dispatchEvent(event)
    elInputRef.current.focus()
    selectionStart.current = elInputRef.current.selectionStart
    selectionEnd.current = elInputRef.current.selectionEnd

    typeof onSelectEmoticon === 'function' && onSelectEmoticon(emoji)
  }

  return (
    <div
      className={classnames('c-input-with-emoji', {
        'c-input-with-emoji--active': active,
        'c-input-with-emoji--danger': variant === 'danger' && !disabled,
        'c-input-with-emoji--disabled': disabled
      })}
      ref={ref}
      onClick={() => setActive(true)}
    >
      <div className='c-input-with-emoji__input' ref={elInputWrapperRef}>
        <InputComponent
          {...props}
          variant={variant}
          disabled={disabled}
          onChange={handleChangeInputComponent}
          onClick={handleClickInputComponent}
        />
      </div>
      <EmojiPicker
        onSelect={handleSelect}
        variant={variant}
        disabled={disabled}
      />
    </div>
  )
}
